import { useAsyncData, useNuxtApp } from '#imports'
import { useDataCache } from '#nuxt-multi-cache/composables'

function valueOrMethod<T>(value: T | ((result: any) => T), result: any): T {
  if (typeof value === 'function') {
    return (value as (v: any) => T)(result)
  }
  return value
}

function isValidMaxAge(v: unknown): v is number {
  return typeof v === 'number' && v >= 1
}

type CachedAsyncDataOptions = {
  clientMaxAge?: number
  serverMaxAge?: number | ((result: any) => number)
  serverCacheTags?: string[] | ((result: any) => string[])
} & Record<string, any>

export function useCachedAsyncData(
  key: string,
  handler: (app?: any) => Promise<any>,
  providedOptions?: CachedAsyncDataOptions,
) {
  const options = providedOptions && typeof providedOptions === 'object' ? providedOptions : {}

  if (import.meta.client) {
    const app = useNuxtApp()
    if (!app.static.data.__firstHydrationTime) {
      app.static.data.__firstHydrationTime = Date.now()
    }

    return useAsyncData(
      key,
      async () => {
        const result = await handler(app)
        const data = options.transform ? await options.transform(result) : result

        if (isValidMaxAge(options.clientMaxAge)) {
          app.static.data[key] = {
            data,
            expires: Date.now() + options.clientMaxAge * 1000,
          }
        }

        return data
      },
      {
        ...options,
        transform: undefined,
        getCachedData(key2: string, nuxtApp: any) {
          const payloadData = nuxtApp.payload.data[key2]
          if (payloadData && app.isHydrating) {
            return payloadData
          }

          if (!isValidMaxAge(options.clientMaxAge)) {
            return undefined
          }

          if (payloadData) {
            const firstHydrationTime = nuxtApp.static.data.__firstHydrationTime
            const expires = firstHydrationTime + options.clientMaxAge * 1000
            if (expires > Date.now()) {
              return payloadData
            }
          }

          const staticCache = app.static.data[key2]
          if (staticCache && staticCache.expires > Date.now()) {
            return staticCache.data
          }

          return undefined
        },
      },
    )
  }

  return useAsyncData(
    key,
    async (app?: any) => {
      const { value, addToCache } = await useDataCache(key, app?.ssrContext?.event)

      if (value) {
        return value
      }

      const result = await handler(app)
      const cacheTags = valueOrMethod(options.serverCacheTags, result) || []
      const maxAge = valueOrMethod(options.serverMaxAge, result)
      const data = options.transform ? await options.transform(result) : result

      if (isValidMaxAge(maxAge)) {
        await addToCache(data, cacheTags, maxAge)
      }

      return data
    },
    {
      ...options,
      transform: undefined,
      getCachedData: undefined,
    },
  )
}
